import {
  getArdoqErrorTraceId,
  isArdoqError,
  toArdoqError,
  type ArdoqError,
} from '@ardoq/common-helpers';
import { confirm, modal } from '@ardoq/modal';
import type {
  BulkEditFieldColumn,
  BulkEditFieldData,
  BulkEditOrDeleteCommands,
} from '../../types';
import { getConfirmMultiUpdateFailedDialog } from '../confirmDialogs';
import { BulkEditFieldDialog } from './BulkEditFieldDialog';
import type { I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';

const showFailure = (i18nLocale: I18nLocale, error: ArdoqError) =>
  confirm(
    getConfirmMultiUpdateFailedDialog(
      i18nLocale,
      error,
      getArdoqErrorTraceId(error)
    )
  );

const executeBulkUpdate = async (
  update: BulkEditFieldData,
  i18nLocale: I18nLocale,
  commands: BulkEditOrDeleteCommands,
  refreshServerSideForGridCallback: () => void
): Promise<void> => {
  const result = await commands.executeBulkUpdate(update);
  if (isArdoqError(result)) {
    showFailure(i18nLocale, result);
    return;
  }
  refreshServerSideForGridCallback();
};

export const openBulkEditFieldDialog = async ({
  preselectedColumnKey,
  i18nLocale,
  locale,
  commands,
  refreshServerSideForGridCallback,
  allBulkEditableColumns,
}: {
  preselectedColumnKey: string | null;
  i18nLocale: I18nLocale;
  locale: Locale;
  commands: BulkEditOrDeleteCommands;
  refreshServerSideForGridCallback: () => void;
  allBulkEditableColumns: BulkEditFieldColumn[];
}): Promise<void> => {
  try {
    if (allBulkEditableColumns.length === 0) {
      return;
    }

    const update = await modal<BulkEditFieldData>(resolve => (
      <BulkEditFieldDialog
        columns={allBulkEditableColumns}
        initialColumnKey={
          preselectedColumnKey ?? allBulkEditableColumns[0].columnKey
        }
        getBulkEditAffectedComponentCount={
          commands.getBulkEditAffectedComponentCount
        }
        i18nLocale={i18nLocale}
        locale={locale}
        onCancel={() => resolve(false)}
        onSubmit={resolve}
      />
    ));

    if (!update) {
      return;
    }

    await executeBulkUpdate(
      update,
      i18nLocale,
      commands,
      refreshServerSideForGridCallback
    );
  } catch (error) {
    showFailure(i18nLocale, toArdoqError({ error: error as Error }));
  }
};
